import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LogOut,
  Search,
  Download,
  Mail,
  Phone,
  Building2,
  MessageSquare,
  RefreshCw,
  Loader2,
  Inbox,
  Users as UsersIcon,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminContacts,
  adminEmailLeads,
  adminMe,
  adminExportUrl,
  clearToken,
  getToken,
} from "@/lib/api";

const PKG_LABEL = {
  week: "1 viikko",
  month: "1 kuukausi",
  year: "12 kuukautta",
  unspecified: "—",
};

const fmtDate = (v) => {
  try {
    const d = new Date(v);
    return d.toLocaleString("fi-FI", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(v);
  }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [tab, setTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const logout = () => {
    clearToken();
    navigate("/admin/login", { replace: true });
  };

  const load = async () => {
    setLoading(true);
    try {
      const [c, l] = await Promise.all([adminContacts(), adminEmailLeads()]);
      setContacts(c);
      setLeads(l);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Lataus epäonnistui.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Admin · Yhteydenotot | Ysitien LED";
    if (!getToken()) {
      navigate("/admin/login", { replace: true });
      return;
    }
    adminMe()
      .then((u) => {
        setAdmin(u);
        return load();
      })
      .catch(() => {
        clearToken();
        navigate("/admin/login", { replace: true });
      });
  }, []);

  const filteredContacts = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return contacts;
    return contacts.filter((c) =>
      [c.name, c.company, c.email, c.phone, c.message, c.package]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [contacts, q]);

  const filteredLeads = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return leads;
    return leads.filter((l) =>
      [l.email, l.source].filter(Boolean).join(" ").toLowerCase().includes(needle)
    );
  }, [leads, q]);

  const downloadCsv = async () => {
    try {
      const res = await fetch(adminExportUrl(), {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "yhteydenotot.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("CSV-lataus epäonnistui.");
    }
  };

  const stats = [
    { icon: Inbox, k: contacts.length, v: "Yhteydenottoa" },
    { icon: UsersIcon, k: leads.length, v: "Sähköpostiliidiä" },
    {
      icon: MessageSquare,
      k: contacts.filter((c) => c.package === "year").length,
      v: "12 kk pakettikysely",
    },
  ];

  return (
    <div className="min-h-screen bg-[#07111F] text-white">
      <header className="border-b border-[#1E2F44] bg-[#0A1729]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#22D3EE] text-[#07111F] font-black text-sm">
              9
            </span>
            <span className="font-display text-lg font-bold tracking-tight">
              Ysitien <span className="text-[#22D3EE]">LED</span>{" "}
              <span className="text-neutral-500 text-sm">/ admin</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-xs text-neutral-400">
              {admin?.email}
            </span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-md border border-[#1E2F44] bg-transparent px-3 py-1.5 text-sm text-neutral-300 hover:bg-white/5"
            >
              <LogOut className="h-4 w-4" /> Kirjaudu ulos
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold">Yhteydenotot</h1>
            <p className="mt-2 text-neutral-400 text-sm">
              Kaikki verkkosivun kautta tulleet yhteydenotot ja sähköpostiliidit.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 rounded-md border border-[#1E2F44] bg-transparent px-4 py-2 text-sm text-neutral-300 hover:bg-white/5"
            >
              <RefreshCw className="h-4 w-4" /> Päivitä
            </button>
            <button
              onClick={downloadCsv}
              data-testid="admin-csv"
              className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-[#E0B14C] to-[#F1C46A] px-4 py-2 text-sm font-bold text-[#1A1206] hover:brightness-110 shadow-md shadow-[#E0B14C]/30"
            >
              <Download className="h-4 w-4" /> Vie CSV
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {stats.map((s, i) => (
            <div
              key={i}
              className="rounded-2xl border border-[#1E2F44] bg-[#0D1B2A] p-5 flex items-center gap-4"
              data-testid={`admin-stat-${i}`}
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#22D3EE]/10 text-[#22D3EE] border border-[#22D3EE]/30">
                <s.icon className="h-5 w-5" />
              </span>
              <div>
                <div className="font-display text-2xl font-bold">{s.k}</div>
                <div className="text-xs text-neutral-400 uppercase tracking-wider">
                  {s.v}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs + search */}
        <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="inline-flex rounded-lg border border-[#1E2F44] bg-[#0D1B2A] p-1">
            {[
              { id: "contacts", label: `Lomake (${contacts.length})` },
              { id: "leads", label: `Popup (${leads.length})` },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  tab === t.id
                    ? "bg-[#22D3EE] text-[#07111F]"
                    : "text-neutral-300 hover:bg-white/5"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="relative w-full md:max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Hae nimellä, yrityksellä, emaililla…"
              data-testid="admin-search"
              className="w-full rounded-md border border-[#1E2F44] bg-[#0D1B2A] pl-10 pr-3 py-2.5 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:ring-2 focus:ring-[#22D3EE]"
            />
          </div>
        </div>

        {/* Content */}
        <div className="mt-6">
          {loading ? (
            <div className="rounded-2xl border border-[#1E2F44] bg-[#0D1B2A] p-16 text-center text-neutral-400">
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#22D3EE]" />
              <div className="mt-3 text-sm">Ladataan…</div>
            </div>
          ) : tab === "contacts" ? (
            <ContactsTable rows={filteredContacts} />
          ) : (
            <LeadsTable rows={filteredLeads} />
          )}
        </div>
      </main>
    </div>
  );
}

const ContactsTable = ({ rows }) => {
  if (rows.length === 0)
    return (
      <div
        data-testid="admin-empty-contacts"
        className="rounded-2xl border border-[#1E2F44] bg-[#0D1B2A] p-16 text-center text-neutral-400"
      >
        Ei yhteydenottoja vielä.
      </div>
    );
  return (
    <div className="overflow-x-auto rounded-2xl border border-[#1E2F44] bg-[#0D1B2A]">
      <table className="w-full min-w-[900px] text-left text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-neutral-400 border-b border-[#1E2F44]">
            <th className="p-4">Aika</th>
            <th className="p-4">Nimi</th>
            <th className="p-4">Yritys</th>
            <th className="p-4">Yhteystiedot</th>
            <th className="p-4">Paketti</th>
            <th className="p-4">Viesti</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              data-testid={`contact-row-${r.id}`}
              className="border-b border-[#1E2F44]/60 last:border-0 hover:bg-white/[0.02] align-top"
            >
              <td className="p-4 text-neutral-400 whitespace-nowrap">{fmtDate(r.created_at)}</td>
              <td className="p-4 font-medium text-white">{r.name}</td>
              <td className="p-4 text-neutral-300">
                <span className="inline-flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5 text-[#22D3EE]" /> {r.company}
                </span>
              </td>
              <td className="p-4">
                <a
                  href={`mailto:${r.email}`}
                  className="flex items-center gap-1.5 text-neutral-200 hover:text-[#22D3EE]"
                >
                  <Mail className="h-3.5 w-3.5 text-[#22D3EE]" /> {r.email}
                </a>
                <a
                  href={`tel:${r.phone}`}
                  className="mt-1 flex items-center gap-1.5 text-neutral-400 hover:text-[#22D3EE] text-xs"
                >
                  <Phone className="h-3.5 w-3.5 text-[#22D3EE]" /> {r.phone}
                </a>
              </td>
              <td className="p-4">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                    r.package === "year"
                      ? "bg-[#E0B14C]/15 text-[#F1C46A] border border-[#E0B14C]/40"
                      : "bg-[#22D3EE]/10 text-[#67E8F9] border border-[#22D3EE]/30"
                  }`}
                >
                  {PKG_LABEL[r.package] || r.package}
                </span>
              </td>
              <td className="p-4 text-neutral-300 max-w-md whitespace-pre-wrap break-words">
                {r.message || <span className="text-neutral-600">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const LeadsTable = ({ rows }) => {
  if (rows.length === 0)
    return (
      <div
        data-testid="admin-empty-leads"
        className="rounded-2xl border border-[#1E2F44] bg-[#0D1B2A] p-16 text-center text-neutral-400"
      >
        Ei sähköpostiliidejä vielä.
      </div>
    );
  return (
    <div className="overflow-x-auto rounded-2xl border border-[#1E2F44] bg-[#0D1B2A]">
      <table className="w-full min-w-[560px] text-left text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-neutral-400 border-b border-[#1E2F44]">
            <th className="p-4">Aika</th>
            <th className="p-4">Sähköposti</th>
            <th className="p-4">Lähde</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              data-testid={`lead-row-${r.id}`}
              className="border-b border-[#1E2F44]/60 last:border-0 hover:bg-white/[0.02]"
            >
              <td className="p-4 text-neutral-400 whitespace-nowrap">{fmtDate(r.created_at)}</td>
              <td className="p-4">
                <a
                  href={`mailto:${r.email}`}
                  className="inline-flex items-center gap-1.5 text-white hover:text-[#22D3EE]"
                >
                  <Mail className="h-3.5 w-3.5 text-[#22D3EE]" /> {r.email}
                </a>
              </td>
              <td className="p-4 text-neutral-400">{r.source}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
